import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Search, HelpCircle, ArrowUpRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { faqCategories, images } from '../mock/mock';
import { useContent } from '../context/ContentContext';

function FaqItem({ q, a, open, onToggle, idx }) {
  return (
    <div
      className="reveal rounded-2xl bg-[#EDEAE0] overflow-hidden transition-shadow duration-300 hover:shadow-[0_10px_30px_-18px_rgba(44,58,40,0.5)]"
      style={{ transitionDelay: `${idx * 40}ms` }}
      data-testid="faq-item"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 text-left px-6 sm:px-7 py-5"
        data-testid="faq-question-toggle"
        aria-expanded={open}
      >
        <span className="flex-1 font-display text-lg sm:text-xl font-semibold text-[#2C3A28] leading-snug">
          {q}
        </span>
        <span
          className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full transition-all duration-300 ${
            open ? 'bg-[#2C3A28] text-white rotate-180' : 'bg-[#E9EBED] text-[#2C3A28]'
          }`}
        >
          <ChevronDown size={18} />
        </span>
      </button>
      <div
        className={`grid transition-all duration-400 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-6 sm:px-7 pb-6 -mt-1 text-[#3B2A20]/75 text-[15px] sm:text-base leading-relaxed max-w-2xl">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Faq() {
  const { content, txt } = useContent();
  const allCats = content.faqs && content.faqs.length ? content.faqs : faqCategories;
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState('about-nut-milk-0');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCats;
    return allCats
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query, allCats]);

  useReveal([query, openKey]);

  const totalMatches = filtered.reduce((n, c) => n + c.items.length, 0);

  return (
    <div className="bg-[#E9EBED]" data-testid="faq-page">
      <div className="pt-28" />
      {/* header, hidden; remove the spacer above and "hidden" below to reinstate */}
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
        <img
          src={images.pour}
          alt="Pouring fresh nut milk"
          className="absolute inset-0 w-full h-full object-cover object-[center_35%] opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="inline-flex items-center gap-2 text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            <HelpCircle size={16} /> Good to know
          </span>
          <h1 className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            Frequently asked questions
          </h1>
          <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
            Everything about our fresh nut milk, the café, storage, sourcing and
            wholesale, answered.
          </p>
        </div>
      </section>

      {/* content */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="mb-12 flex items-center gap-2 rounded-full bg-[#EDEAE0] px-5 py-3.5 w-full sm:w-96 focus-within:ring-2 focus-within:ring-[#2C3A28]/40 transition-shadow">
            <Search size={18} className="text-[#3B2A20]/50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions..."
              className="flex-1 bg-transparent outline-none text-[#2C3A28] placeholder:text-[#3B2A20]/40"
              data-testid="faq-search-input"
            />
          </div>

          {filtered.map((cat) => (
            <div key={cat.id || cat.label} className="mb-12 last:mb-0">
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2C3A28]">
                  {cat.label}
                </h2>
                <span className="h-px flex-1 bg-[#2C3A28]/12" />
                <span className="text-xs uppercase tracking-[0.15em] text-[#2C3A28] font-semibold">
                  {cat.items.length}
                </span>
              </div>
              <div className="space-y-3">
                {cat.items.map((it, i) => {
                  const key = `${cat.id || cat.label}-${i}`;
                  return (
                    <FaqItem
                      key={key}
                      q={it.q}
                      a={it.a}
                      idx={i}
                      open={openKey === key}
                      onToggle={() => setOpenKey(openKey === key ? null : key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {totalMatches === 0 && (
            <p className="text-center text-[#3B2A20]/60 py-10" data-testid="faq-no-results">
              No questions match “{query}”. Try another word.
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="reveal rounded-[2rem] bg-[#2C3A28] text-[#E9EBED] px-8 sm:px-12 py-12 text-center overflow-hidden relative">
            <div className="grain" />
            <div className="relative">
              <h3 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">
                {txt('faq', 'ctaHeading', 'Still have a question?')}
              </h3>
              <p className="mt-4 text-[#EDEAE0]/80 max-w-lg mx-auto">
                Whether it's about a wholesale order or where to find us, we're happy to help.
              </p>
              <Link
                to="/where-to-buy"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-7 py-3.5 font-semibold hover:bg-[#2C3A28] transition-colors hover:-translate-y-0.5 duration-300"
                data-testid="faq-contact-cta"
              >
                Get in touch
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
